import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, concatMap, exhaustMap, filter, map, mergeMap, Observable, of, switchMap, takeUntil, takeWhile, timer } from "rxjs";
import { AttioClient, AttioRecordMigrationPreview, AttioRecordMigrationStatus, AttioRecordMigrationState as RunState } from "src/api-service";
import { normalizeError } from "../../../../app-store-error.types";
import { AttioRecordMigrationActions } from "./attio-record-migration.actions";
import { AttioRecordMigrationKind } from "./attio-record-migration.state";

/** How often a running migration's status is fetched. */
const POLL_INTERVAL_MS = 3000;

@Injectable()
export class AttioRecordMigrationEffects {
    private readonly actions$ = inject(Actions);
    private readonly attioClient = inject(AttioClient);

    loadPreview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttioRecordMigrationActions.loadPreview),
            mergeMap(({ kind }) =>
                this.preview(kind).pipe(
                    map(preview => AttioRecordMigrationActions.loadPreviewSuccess({ kind, preview })),
                    catchError(error =>
                        of(AttioRecordMigrationActions.loadPreviewFailure({
                            kind,
                            error: normalizeError(error),
                            toastKey: "DEVELOPER.ATTIO_RECORD_MIGRATION.PREVIEW_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    start$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttioRecordMigrationActions.start),
            concatMap(({ kind }) =>
                this.start(kind).pipe(
                    map(status => AttioRecordMigrationActions.startSuccess({
                        kind,
                        status,
                        toastKey: "DEVELOPER.ATTIO_RECORD_MIGRATION.START_SUCCESS",
                    })),
                    catchError(error =>
                        of(AttioRecordMigrationActions.startFailure({
                            kind,
                            error: normalizeError(error),
                            toastKey: "DEVELOPER.ATTIO_RECORD_MIGRATION.START_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    /** A started run, or one refused because another migration runs (409), is followed until it finishes. */
    pollAfterStart$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttioRecordMigrationActions.startSuccess, AttioRecordMigrationActions.startFailure),
            map(({ kind }) => AttioRecordMigrationActions.startPolling({ kind })),
        ),
    );

    /** Fetches the status now, then every few seconds while the run is running; one poll per kind. */
    poll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttioRecordMigrationActions.startPolling),
            mergeMap(({ kind }) => this.pollOnce$(kind)),
        ),
    );

    private pollOnce$(kind: AttioRecordMigrationKind) {
        const restarted$ = this.actions$.pipe(ofType(AttioRecordMigrationActions.startPolling, AttioRecordMigrationActions.stopPolling), filter(a => a.kind === kind));
        return timer(0, POLL_INTERVAL_MS).pipe(
            exhaustMap(() => this.status(kind)),
            takeWhile(status => status.state === RunState.RUNNING, true),
            map(status => AttioRecordMigrationActions.loadStatusSuccess({ kind, status })),
            catchError(error =>
                of(AttioRecordMigrationActions.loadStatusFailure({
                    kind,
                    error: normalizeError(error),
                    toastKey: "DEVELOPER.ATTIO_RECORD_MIGRATION.STATUS_ERROR",
                })),
            ),
            takeUntil(restarted$),
        );
    }

    private preview(kind: AttioRecordMigrationKind): Observable<AttioRecordMigrationPreview> {
        return kind === "company" ? this.attioClient.previewCompanyMigration() : this.attioClient.previewContactMigration();
    }

    private start(kind: AttioRecordMigrationKind): Observable<AttioRecordMigrationStatus> {
        return kind === "company" ? this.attioClient.startCompanyMigration() : this.attioClient.startContactMigration();
    }

    private status(kind: AttioRecordMigrationKind): Observable<AttioRecordMigrationStatus> {
        return kind === "company" ? this.attioClient.getCompanyMigrationStatus() : this.attioClient.getContactMigrationStatus();
    }
}
